import {
  type FillSource,
  type SmartFillBucket,
  type BucketListEntry,
  type Workspace
} from '@oysterjs/smartfill-contracts';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useSidebarSession } from '../bridge/session';
import { displayError, type DisplayError } from '../bridge/errors';

interface RemoteList<T> {
  items: T[];
  loading: boolean;
  error: DisplayError | null;
  reload: () => void;
}

export function useBucketList(query: string, workspaceId?: string): RemoteList<BucketListEntry> {
  const { client } = useSidebarSession();
  const load = useCallback(
    (normalized: string) =>
      workspaceId
        ? client.request('workspaces.buckets', { workspaceId })
        : client.request(
            'buckets.list',
            normalized ? { query: normalized, limit: 100 } : { limit: 100 }
          ),
    [client, workspaceId]
  );
  return useRemoteList(query, 200, load, 'Could not load buckets.');
}

export type WorkspaceUserSelection = 'me' | 'all' | { userId: string };

export function useWorkspaceList(
  query: string,
  selection: WorkspaceUserSelection = 'me'
): RemoteList<Workspace> {
  const { client } = useSidebarSession();
  const userId = selection === 'me' ? undefined : selection === 'all' ? null : selection.userId;
  const load = useCallback(
    (normalized: string) =>
      client.request('workspaces.list', {
        ...(normalized ? { query: normalized } : {}),
        limit: 50,
        ...(userId !== undefined ? { userId } : {})
      }),
    [client, userId]
  );
  return useRemoteList(query, 250, load, 'Could not load workspaces.');
}

function useRemoteList<T>(
  query: string,
  debounceMs: number,
  load: (normalizedQuery: string) => Promise<T[]>,
  errorMessage: string
): RemoteList<T> {
  const [items, setItems] = useState<T[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<DisplayError | null>(null);
  const [reloadToken, setReloadToken] = useState(0);
  const sequence = useRef(0);

  useEffect(() => {
    const requestSequence = ++sequence.current;
    const normalized = query.trim();
    setLoading(true);
    setError(null);
    setItems([]);
    const timer = window.setTimeout(
      () => {
        void load(normalized)
          .then((result) => {
            if (sequence.current !== requestSequence) return;
            setItems(result);
            setLoading(false);
          })
          .catch((requestError: unknown) => {
            if (sequence.current !== requestSequence) return;
            setError(displayError(requestError, errorMessage));
            setLoading(false);
          });
      },
      normalized ? debounceMs : 0
    );
    return () => {
      window.clearTimeout(timer);
      sequence.current++;
    };
  }, [debounceMs, errorMessage, load, query, reloadToken]);

  return {
    items,
    loading,
    error,
    reload: useCallback(() => setReloadToken((value) => value + 1), [])
  };
}

export function useBucket(bucketId: string | undefined, refreshOnLearning = false) {
  const { client } = useSidebarSession();
  const [bucket, setBucket] = useState<SmartFillBucket | null>(null);
  const [loading, setLoading] = useState(Boolean(bucketId));
  const [error, setError] = useState<DisplayError | null>(null);
  const [reloadToken, setReloadToken] = useState(0);
  const sequence = useRef(0);
  const observedRevision = useRef<{ bucketId: string; revision: number } | null>(null);

  useEffect(() => {
    // Read-only previews follow learning revisions; an open bucket editor
    // retains its draft/revision until its own save or explicit refresh.
    if (!refreshOnLearning) return;
    return client.subscribe('learning.changed', (learning) => {
      if (!bucketId) return;
      const revision = Math.max(
        0,
        ...learning.operations
          .filter((operation) => operation.bucketId === bucketId)
          .map((operation) => operation.bucketRevision)
      );
      const previous =
        observedRevision.current?.bucketId === bucketId ? observedRevision.current.revision : 0;
      if (revision <= previous) return;
      observedRevision.current = { bucketId, revision };
      setReloadToken((value) => value + 1);
    });
  }, [bucketId, client, refreshOnLearning]);

  useEffect(() => {
    const requestSequence = ++sequence.current;
    if (!bucketId) {
      setBucket(null);
      setLoading(false);
      setError({
        code: 'NOT_FOUND',
        message: 'This bucket could not be found.',
        retryable: false
      });
      return;
    }
    setLoading(true);
    setError(null);
    setBucket((current) => (current?.id === bucketId ? current : null));
    void client
      .request('buckets.get', { bucketId })
      .then((result) => {
        if (sequence.current !== requestSequence) return;
        const previous =
          observedRevision.current?.bucketId === result.id ? observedRevision.current.revision : 0;
        observedRevision.current = {
          bucketId: result.id,
          revision: Math.max(previous, result.revision)
        };
        setBucket(result);
        setLoading(false);
      })
      .catch((requestError: unknown) => {
        if (sequence.current !== requestSequence) return;
        setBucket(null);
        setError(displayError(requestError, 'Could not load this bucket.'));
        setLoading(false);
      });
    return () => {
      sequence.current++;
    };
  }, [bucketId, client, reloadToken]);

  return {
    bucket: bucket?.id === bucketId ? bucket : null,
    setBucket,
    loading,
    error,
    reload: useCallback(() => setReloadToken((value) => value + 1), [])
  };
}

export function useActivateBucket() {
  const { selectActiveBucket } = useSidebarSession();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<DisplayError | null>(null);
  const sequence = useRef(0);

  const activate = useCallback(
    async (bucketId: string): Promise<FillSource | null> => {
      const requestSequence = ++sequence.current;
      setBusy(true);
      setError(null);
      try {
        const source = await selectActiveBucket(bucketId);
        if (sequence.current === requestSequence) {
          setBusy(false);
        }
        return source;
      } catch (requestError) {
        if (sequence.current === requestSequence) {
          setError(displayError(requestError, 'Could not switch the active bucket.'));
          setBusy(false);
        }
        return null;
      }
    },
    [selectActiveBucket]
  );

  return { activate, busy, error };
}

/**
 * Deletes one bucket. The worker publishes any resulting active-source change.
 * Resolves true on success so callers can close confirmations or navigate.
 */
export function useDeleteBucket() {
  const { client } = useSidebarSession();
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [error, setError] = useState<DisplayError | null>(null);

  const remove = async (bucketId: string): Promise<boolean> => {
    if (deletingId) return false;
    setDeletingId(bucketId);
    setError(null);
    try {
      await client.request('buckets.delete', { bucketId });
      return true;
    } catch (requestError) {
      setError(displayError(requestError, 'Could not delete this bucket.'));
      return false;
    } finally {
      setDeletingId(null);
    }
  };

  return { remove, deletingId, error };
}

/**
 * The busy/error plumbing every bridge mutation repeats: one action in
 * flight at a time, failures rendered as DisplayError, busy always cleared.
 * run resolves the work's value, or null when it failed or was skipped.
 */
export function useBridgeAction() {
  const keyed = useKeyedBridgeAction<'action'>();
  const run = useCallback(
    <T>(fallbackMessage: string, work: () => Promise<T>) =>
      keyed.run('action', fallbackMessage, work),
    [keyed]
  );
  return { run, busy: keyed.busy !== null, error: keyed.error, clearError: keyed.clearError };
}

/** useBridgeAction for handler groups that track which action is in flight. */
export function useKeyedBridgeAction<K extends string>() {
  const [busy, setBusy] = useState<K | null>(null);
  const [error, setError] = useState<DisplayError | null>(null);
  const inFlight = useRef(false);

  const run = useCallback(
    async <T>(key: K, fallbackMessage: string, work: () => Promise<T>): Promise<T | null> => {
      if (inFlight.current) return null;
      inFlight.current = true;
      setBusy(key);
      setError(null);
      try {
        return await work();
      } catch (requestError) {
        setError(displayError(requestError, fallbackMessage));
        return null;
      } finally {
        inFlight.current = false;
        setBusy(null);
      }
    },
    []
  );
  const clearError = useCallback(() => setError(null), []);
  return { run, busy, error, clearError };
}
